import axios from 'axios';
import { store } from '../store';
import { router } from '../router';


// Lazy load AuthAPIService to avoid circular import risk
let cachedAuth = null;
function getAuthAPIService() {
  if (!cachedAuth) {
    cachedAuth = require('./AuthAPIService').default;
  }
  return cachedAuth;
}
// Central error handler — used across all API methods and axios interceptors.
// Handles 401s (e.g., timeout), suppresses expected, rethrows others.
function handleError(e) {
  if (!e || !e.response) {
    return Promise.reject(e);
  }
  try {
    // Extract status and data once, guard against undefined
    const status = e.response.status;
    const errorData = e.response.data;

    if (status === 401) {
      const isCrossTabLogout = store.state.loggedIn === false;

      if (isCrossTabLogout && process.env.NODE_ENV === 'development') {
        return; // Don't rethrow — error is expected
      }

      if (errorData && errorData.status === 'TIMED_OUT') {
        console.debug('Session timed out, redirecting to /idle...');
        const current = router.currentRoute.path;
        if (current !== '/idle' && current !== '/login') {
          setTimeout(() => router.replace('/idle'), 0);// Skip race
        }
        return;
      }

      if (store.state.loggedIn) {
        console.debug('Logging out user due to 401...');
        store.dispatch('LOGOUT');
        const current = router.currentRoute.path;
        if (current !== '/login') {
          router.replace({ name: 'Login' }); // Named route to avoid back stack
        } else {
          console.debug('Already on /login, no redirect.');
        }
      }

      return; // Don't rethrow — handled 401s gracefully
    }
  } catch (err) {
    console.warn('handleError threw:', err); // Fail-safe logging
  }

  // Always propagate non-401 errors or malformed error objects
  return Promise.reject(e);
}

// Reset activity timer for all outgoing requests unless already timed out
let lastActiveTimerResetAt = 0;
const ACTIVE_TIMER_RESET_THROTTLE_MS = 1000; // performance: avoid thrashing store/sessionStorage during request bursts
axios.interceptors.request.use(
  (config) => {
    if (!store.state.timedOut) {
      const now = Date.now();
      if (now - lastActiveTimerResetAt > ACTIVE_TIMER_RESET_THROTTLE_MS) {
        lastActiveTimerResetAt = now;
        store.dispatch('RESET_ACTIVE_TIMER').catch(() => {});
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);
// Global response handler for catching 401s (e.g. logout, session expiration)
axios.interceptors.response.use(
  response => response,
  error => {
    try {
      return handleError(error); // Returns undefined if 401 handled
    } catch (e) {
      console.warn('handleError threw in response interceptor:', e);
    }
    return Promise.reject(error);
  }
);

export class CommonAPIService {
  constructor() {
    this.api_url = process.env.VUE_APP_API_URL;
  }

  shouldRetrySameOrigin(url, fullUrl, error) {
    if (!url || typeof url !== 'string' || !url.startsWith('/api')) return false;
    if (!fullUrl || typeof fullUrl !== 'string' || !/^https?:\/\//i.test(fullUrl)) return false;
    if (!error || error.response) return false; // only network/CORS-style failures
    if (typeof window === 'undefined' || !window.location || !window.location.origin) return false;
    try {
      const apiOrigin = new URL(fullUrl).origin;
      return apiOrigin !== window.location.origin;
    } catch (e) {
      return false;
    }
  }

  postCall(url, parameters, router) {
    const fullUrl = this.api_url + url;
    return axios.post(fullUrl, parameters, { withCredentials: true })
      // 4 Feb 2026 JCH When a 401 is handled by the interceptor, axios may resolve `undefined`
      .then(response => (response ? response.data : undefined))
      .catch((error) => {
        return handleError(error); // Handles + returns rejection if needed
      });
  }

  fileCall(url, files, router) {
    const fullUrl = this.api_url + url;
    return axios.post(fullUrl, files,
      {
        headers: {'Content-Type': 'multipart/form-data'},
        withCredentials: true
      })
      .then(response => response)
      .catch((error) => {
        return handleError(error); // Handles + returns rejection if needed
      });
  }

  deleteCall(url, router) {
    const fullUrl = this.api_url + url;
    return axios.delete(fullUrl, { withCredentials: true })
      // 4 Feb 2026 JCH When a 401 is handled by the interceptor, axios may resolve `undefined`
      .then(response => (response ? response.data : undefined))
      .catch((error) => {
        return handleError(error); // Handles + returns rejection if needed
      });
  }

  putCall(url, parameters, router) {
    const fullUrl = this.api_url + url;
    // 11 Mar 2026 JCH: On dev hosts, try same-origin '/api' first for cross-origin PUT to reduce CORS noise.
    const trySameOriginFirst = !!(
      url && typeof url === 'string' && url.startsWith('/api') &&
      fullUrl && /^https?:\/\//i.test(fullUrl) &&
      typeof window !== 'undefined' && window.location && window.location.origin &&
      new URL(fullUrl).origin !== window.location.origin
    );
    const primaryUrl = trySameOriginFirst ? url : fullUrl;
    const fallbackUrl = trySameOriginFirst ? fullUrl : url;
    const doPut = (targetUrl) => axios.put(targetUrl, parameters, { withCredentials: true })
      .then(response => (response ? response.data : undefined));

    return doPut(primaryUrl).catch((error) => {
      // 11 Mar 2026 JCH: Dev CORS workaround - for cross-origin PUT, try same-origin '/api' first, then fallback.
      if (trySameOriginFirst && error && !error.response) {
        return doPut(fallbackUrl).catch((retryError) => handleError(retryError));
      }
      return handleError(error); // Handles + returns rejection if needed
    });
  }

  getCall(url, router) {
    const fullUrl = this.api_url + url;
    return axios.get(fullUrl, { withCredentials: true })
      // 4 Feb 2026 JCH When a 401 is handled by the interceptor, axios may resolve `undefined`
      .then(response => (response ? response.data : undefined))
      .catch((error) => {
        return handleError(error); // Handles + returns rejection if needed
      });
  }
}

export default CommonAPIService;
